import { useMemo } from 'react'
import type { DebugInfoTarget } from '@renderer/components/help-menu/use-copy-debug-info'
import { resolveActionSpanLink } from '../components/braintrust-links'
import { useCanViewBraintrustLinks } from '../components/braintrust-span-link'
import { useConversationBraintrustLink } from '../components/use-conversation-braintrust-link'
import { useViewNavigation } from '../view-atoms'
import { viewContextOf, type OpenView } from '../view-definitions'
import { useActionViewModel } from '../views/action-view/use-action-view-model'
import { plannerVersion } from './staged-actions/confidence'
import type { StagedAction } from './use-staged-actions'

/** Where the open view's Braintrust trace comes from. */
export type BraintrustTrace = { kind: 'conversation'; conversationId: string } | { kind: 'span'; link: string }

export interface TopViewDebugInfo {
  /** The ids a support report needs to find the open view's traces. */
  target: DebugInfoTarget
  trace?: BraintrustTrace
}

const conversationTrace = (conversationId: string | undefined): BraintrustTrace | undefined =>
  conversationId ? { kind: 'conversation', conversationId } : undefined

const spanTrace = (link: string | undefined): BraintrustTrace | undefined => (link ? { kind: 'span', link } : undefined)

/** Opaque ids only: the span link stays behind the staff-only `braintrustLink`. */
function actionProvenance(action: StagedAction): DebugInfoTarget {
  return {
    planner: plannerVersion(action),
    jobId: action.jobId ?? undefined,
    audioSessionId: action.references?.find((reference) => reference.audioSessionId)?.audioSessionId,
    traceId: action.metadata?.traceId,
    plannerSpanId: action.metadata?.plannerSpanId,
  }
}

/**
 * A chat traces to its latest turn. An action traces to its authoring span —
 * or, while its edit chat is open, that chat's latest turn, since that's the
 * flow being debugged.
 */
/** `actionChatId` is the Action View's resolved edit-thread id, which covers the refetch window after a submit. */
export function resolveTopViewDebugInfo(
  view: OpenView,
  action: StagedAction | undefined,
  actionChatId?: string,
): TopViewDebugInfo {
  const chatId = viewContextOf(view, 'chat')?.chatId ?? viewContextOf(view, 'chatOutput')?.conversationId
  if (chatId) return { target: { conversationId: chatId }, trace: conversationTrace(chatId) }

  const actionContext = viewContextOf(view, 'action')
  if (!actionContext) return { target: {} }
  const conversationId = actionChatId
  const spanLink = action ? resolveActionSpanLink(action, actionContext.braintrustLink) : actionContext.braintrustLink
  return {
    target: { actionId: actionContext.actionId, conversationId, ...(action && actionProvenance(action)) },
    trace: actionContext.chat ? conversationTrace(conversationId) : spanTrace(spanLink),
  }
}

/** The open view's debug ids, plus its Braintrust link for staff. */
export function useTopViewDebugInfo(): DebugInfoTarget {
  const { topView } = useViewNavigation()
  const { action, chatId } = useActionViewModel(viewContextOf(topView, 'action')?.actionId)
  const { target, trace } = useMemo(() => resolveTopViewDebugInfo(topView, action, chatId), [topView, action, chatId])
  const braintrustLink = useBraintrustTraceLink(trace)
  return useMemo(() => (braintrustLink ? { ...target, braintrustLink } : target), [target, braintrustLink])
}

/** Staff-only: span traces carry their link; conversation traces are looked up. */
function useBraintrustTraceLink(trace: BraintrustTrace | undefined): string | undefined {
  const canViewBraintrustLinks = useCanViewBraintrustLinks()
  const conversationLink = useConversationBraintrustLink(
    trace?.kind === 'conversation' ? trace.conversationId : undefined,
  )
  if (!canViewBraintrustLinks) return undefined
  return (trace?.kind === 'span' ? trace.link : conversationLink) ?? undefined
}
